import React, { useCallback, useMemo, useState } from 'react';
import { useAdminAIRateLimits } from '../../agents/aiRateLimitAdminAgent';
import './AllowedEmailsManager.css';
import './AdminAiRateLimitsManager.css';

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function formatWindow(entry) {
  if (entry?.is_expired) {
    return 'Expired';
  }
  if (entry?.window_expires_at) {
    return `Until ${formatDateTime(entry.window_expires_at)}`;
  }
  return '—';
}

export default function AdminAiRateLimitsManager() {
  const { settings, entries, loading, error, isAdmin, resetBucket } = useAdminAIRateLimits();
  const [search, setSearch] = useState('');
  const [busyIdentity, setBusyIdentity] = useState(null);
  const [actionError, setActionError] = useState(null);

  const filteredEntries = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return entries;

    return entries.filter((entry) => {
      const fullName = `${entry.first_name || ''} ${entry.last_name || ''}`.toLowerCase();
      const email = (entry.email || '').toLowerCase();
      const identity = (entry.identity_key || '').toLowerCase();
      return fullName.includes(query) || email.includes(query) || identity.includes(query);
    });
  }, [entries, search]);

  const handleReset = useCallback(async (entry) => {
    setBusyIdentity(entry.identity_key);
    setActionError(null);
    try {
      await resetBucket(entry.identity_key);
    } catch (err) {
      setActionError(err?.message || 'Failed to reset bucket');
    } finally {
      setBusyIdentity(null);
    }
  }, [resetBucket]);

  if (loading) {
    return (
      <div className="admin-shell">
        <div className="admin-loading">Loading AI rate limits...</div>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="admin-shell">
        <div className="admin-forbidden">
          <h2 className="admin-forbidden-title">Access Denied</h2>
          <p>You do not have permission to manage AI rate limits.</p>
        </div>
      </div>
    );
  }

  const enabledClass = settings.enabled ? 'admin-badge--allowed' : 'admin-badge--none';

  return (
    <div className="admin-shell ai-rate-limits-manager">
      <div className="admin-header">
        <h1 className="admin-title">AI Rate Limits</h1>
        <p className="admin-subtitle">
          Review per-user AI quota usage and reset a bucket when a user needs fresh capacity.
        </p>
      </div>

      <section className="admin-section ai-rate-limits-manager__summary">
        <div className="ai-rate-limits-manager__settings">
          <span className={`admin-badge ${enabledClass}`}>
            {settings.enabled ? 'Enabled' : 'Disabled'}
          </span>
          <span className="admin-badge admin-badge--none">
            {settings.max_requests} req / {settings.window_seconds}s
          </span>
        </div>
        <p className="admin-section-desc">
          Buckets are keyed by authenticated user id, then JWT subject, with a shared anonymous bucket for unauthenticated requests.
        </p>
      </section>

      {(error || actionError) && (
        <div className="admin-banner admin-banner--error" role="alert">
          {actionError || error}
        </div>
      )}

      <section className="admin-section">
        <h2 className="admin-section-title">User Buckets</h2>
        <p className="admin-section-desc">
          Search by name, email, or identity key. Reset clears the current window for that bucket.
        </p>

        <div className="admin-add-row">
          <input
            className="admin-input"
            type="search"
            placeholder="Search by user or identity key..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search AI rate limits"
          />
        </div>

        {filteredEntries.length === 0 ? (
          <p className="admin-empty">No buckets match your search.</p>
        ) : (
          <div className="ai-rate-limits-manager__list">
            {filteredEntries.map((entry) => (
              <div key={entry.identity_key} className="ai-rate-limits-manager__row">
                <div className="ai-rate-limits-manager__main">
                  <div className="ai-rate-limits-manager__name-row">
                    <strong>
                      {entry.kind === 'anonymous'
                        ? 'Anonymous bucket'
                        : `${entry.first_name || ''} ${entry.last_name || ''}`.trim() || 'Unnamed user'}
                    </strong>
                    <span className={`admin-badge ${entry.kind === 'anonymous' ? 'admin-badge--none' : 'admin-badge--allowed'}`}>
                      {entry.kind}
                    </span>
                  </div>
                  <div className="ai-rate-limits-manager__meta">
                    <span>{entry.email || 'No email on file'}</span>
                    <span>{entry.identity_key}</span>
                  </div>
                  <div className="ai-rate-limits-manager__meta">
                    <span>Used {entry.request_count} of {settings.max_requests}</span>
                    <span>Remaining {entry.remaining_requests}</span>
                    <span>{formatWindow(entry)}</span>
                    <span>Window starts {formatDateTime(entry.window_started_at)}</span>
                  </div>
                </div>
                <div className="ai-rate-limits-manager__actions">
                  <button
                    type="button"
                    className="admin-btn admin-btn--remove"
                    disabled={busyIdentity === entry.identity_key}
                    onClick={() => handleReset(entry)}
                  >
                    {busyIdentity === entry.identity_key ? 'Resetting...' : 'Reset quota'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
